import React, { ReactNode, useContext } from 'react'
import { Plural, Trans } from '@lingui/macro'
import Tooltip from '../../../../components/common/tooltip/Tooltip'
import { formatDateAndTimeShort } from '../../../../utils/dateHelpers'
import { Box, Link } from 'front-end/src/components'

import { dayjsFromNow } from 'shared/dayjs'
import { TopicDetailContext } from '../../TopicDetailContext'

import { useTopicForEditQuery } from 'front-end/src/pages/topic-edit-page/TopicEditPage'
import { draftIsEqualPublished } from '../utils/draftIsEqualPublished'
import {
  StatusArchivedGrey,
  StatusDraftGrey,
  StatusUnpublishedChangesGrey
} from '../../../../components/common/status/Status'
import { useParams } from 'react-router-dom'
import {
  Byline,
  BylineSection
} from '../../../../components/common/byline/Byline'
import { linkBuilder } from 'shared/linkBuilder'
import { brandColors } from 'shared/basicPalette'
import IconMap from '../../../../components/common/icon/IconMap'
import IconLayers from '../../../../components/common/icon/IconLayers'

type ParentInstance = {
  url: string
  name?: string
  isPathway?: boolean
}

export const TopicByline = (props: {
  topic: any
  version: any
  readTime: any
  unpublishedChanges?: boolean
  wasUpdated?: any
  status?: ReactNode
  parentInstance: ParentInstance | null
}) => {
  const {
    topic,
    version,
    readTime,
    unpublishedChanges,
    wasUpdated,
    status = false,
    parentInstance
  } = props

  return (
    <>
      <Byline pb="4px">
        {(readTime || readTime === 0) && (
          <BylineSection>
            <Plural
              value={readTime}
              _0="< 1 min read"
              /**
                On backend, we round duration seconds UP, for example 55 seconds is rounded UP to 1 minute.
                That's the reason why we show "< 1 min read" for 1 minute duration.
              */
              one="< 1 min read"
              other="# mins read"
            />
          </BylineSection>
        )}
        {topic.author?.display_name && (
          <BylineSection showDot={!!parentInstance}>
            <Trans>by</Trans> {topic.author.display_name}
          </BylineSection>
        )}
        {parentInstance && parentInstance.name && (
          <BylineSection showDot={false} whiteSpace="normal">
            <Link
              color={brandColors.grey['700']}
              to={parentInstance.url}
              display="flex"
              _hover={{ textDecoration: 'none' }}
            >
              {parentInstance.isPathway ? (
                <IconMap alignSelf="center" mr="6px" ml="2px" />
              ) : (
                <IconLayers alignSelf="center" mr="6px" ml="2px" />
              )}
              {parentInstance.name}
            </Link>
          </BylineSection>
        )}
        {status && <BylineSection showDot={false}>{status}</BylineSection>}
      </Byline>

      <Byline>
        {topic.firstPublishedDate && (
          <BylineSection>
            <Tooltip text={formatDateAndTimeShort(topic.firstPublishedDate)}>
              <Trans>Published</Trans> {dayjsFromNow(topic.firstPublishedDate)}
            </Tooltip>
          </BylineSection>
        )}
        {(wasUpdated || unpublishedChanges) && (
          <BylineSection showDot={!!(version && !unpublishedChanges)}>
            {topic.lastPublishedDate && (
              <Tooltip text={formatDateAndTimeShort(topic.lastPublishedDate)}>
                <Trans>Updated</Trans> {dayjsFromNow(topic.lastPublishedDate)}
              </Tooltip>
            )}
          </BylineSection>
        )}
        {version && !unpublishedChanges && (
          <Box whiteSpace="nowrap">
            <Trans>Version</Trans> {version}
          </Box>
        )}
      </Byline>
    </>
  )
}

export const TopicBylineForEditor = () => {
  const params = useParams<{ topicVersionId?: string }>()
  const { data } = useTopicForEditQuery()

  const topic = data?.topic

  if (topic?.__typename === 'TopicModelAsEditor') {
    const unpublishedChanges =
      !!topic.draftVersion &&
      !draftIsEqualPublished(
        topic.currentTopicVersionAsEditor,
        topic.draftVersion
      )
    const isOlderVersion = !!params?.topicVersionId
    const isDraft = !topic.currentTopicVersionAsEditor

    return (
      <TopicByline
        status={
          <>
            {isOlderVersion && <StatusArchivedGrey ml="4px" />}
            {!isDraft && !isOlderVersion && unpublishedChanges && (
              <StatusUnpublishedChangesGrey ml="4px" />
            )}
            {isDraft && <StatusDraftGrey ml="4px" />}
          </>
        }
        topic={data?.topic}
        version={topic.currentTopicVersionAsEditor?.version}
        readTime={topic.draftVersion?.readDurationMins}
        unpublishedChanges={unpublishedChanges}
        wasUpdated={topic.firstPublishedDate !== topic.lastPublishedDate}
        parentInstance={{
          url: linkBuilder.workspaceUrl(topic.workspace_id),
          name: topic.workspace?.name
        }}
      />
    )
  }
  return null
}

export const TopicBylineForStudent = () => {
  const { topic, topicVersion, pathway } = useContext(TopicDetailContext)
  if (!topic) {
    return null
  }

  let parentInstance: ParentInstance | null = null

  if (pathway) {
    parentInstance = {
      url: linkBuilder.pathwayDetail(pathway.id),
      name: pathway.title,
      isPathway: true
    }
  } else {
    if (topic.__typename === 'TopicModelAsEditor' && topic?.workspaceAsEditor) {
      parentInstance = {
        url: linkBuilder.workspaceUrl(topic.workspace_id),
        name: topic['workspaceAsEditor']!.name
      }
    } else if (
      topic.__typename === 'TopicModelAsMember' &&
      topic.isWorkspaceAccessible
    ) {
      // Student might not have access to the workspace even when topic can be accessed.
      // That's because access might be granted by pathway or certification. In such cases
      // workspace is not shown in Byline. That's the reason why isWorkspaceAccessible is checked here.
      parentInstance = {
        url: linkBuilder.workspaceUrl(topic.workspace_id),
        name: topic['workspace']!.name
      }
    }
  }

  return (
    <TopicByline
      status={
        topic.currentTopicVersion?.version !== topicVersion?.version ||
        topic.deleted_at ? (
          <StatusArchivedGrey ml="4px" />
        ) : undefined
      }
      topic={topic}
      version={topicVersion?.version}
      readTime={topicVersion?.readDurationMins}
      wasUpdated={topic.firstPublishedDate !== topic.lastPublishedDate}
      parentInstance={parentInstance}
    />
  )
}
